import * as React from "react";
import { Flex } from "../flex";
import { Tag } from "../tag/tag";
import { HiX } from "react-icons/hi";
import { EMAIL_REGEX } from "../../utils/regex";
import { InputLabel } from "../input-label";
import { useControlledState } from "../../hooks/use-controlled-state";
import { InputError } from "../input-error";
import { IconButton } from "../button";
import { Box } from "../box";
import { useTheme } from "../theme";
import { FieldProps } from "../form/field";

interface TagField extends FieldProps<"input"> {
  tags?: Array<string>;
  defaultTags?: Array<string>;
  placeholder?: string;
  onInputValueChange?(value: string): void;
  onChange?(tags: Array<string>): void;
  tagVariant?: React.ComponentProps<typeof Tag>["variant"];
  tagColor?: React.ComponentProps<typeof Tag>["color"];
  tagShape?: React.ComponentProps<typeof Tag>["shape"];
  tagSize?: React.ComponentProps<typeof Tag>["size"];
  renderTag?(value: string, onRemove: (value: string) => void): React.ReactNode;
}

export function TagInput({
  appearance = "outline",
  size = "md",
  label,
  error,
  placeholder,
  tags,
  defaultTags,
  tagColor = "gray",
  tagVariant = "light",
  tagShape = "rounded",
  tagSize = "md",
  color = "gray",
  onChange,
  renderTag,
  radius,
}: TagField) {
  const [inputValue, setInputValue] = React.useState("");
  const [values, setValues] = useControlledState<Array<string>>(
    dedupe(tags),
    dedupe(defaultTags) || [],
    onChange
  );
  const [isFocused, setIsFocused] = React.useState(false);
  const theme = useTheme();

  function dedupe(values: Array<string> | undefined) {
    if (!values) return undefined;
    return Array.from(new Set(values));
  }

  const inputRef = React.useRef<HTMLInputElement>(null);

  function onInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const { value } = e.target;
    setInputValue(value);
  }

  // function hasTrailingSpace(value: string) {
  //   return /\s$/.test(value);
  // }

  function hasTrailingComma(value: string) {
    return /,$/.test(value);
  }

  function onInputFocus() {
    setIsFocused(true);
  }

  function onInputBlur() {
    setIsFocused(false);
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    switch (e.key) {
      case "Enter":
      case " ":
        if (isValid(inputValue.trim())) {
          commit([inputValue]);
        }
        break;
      case "Backspace":
        if (!inputValue) {
          onRemove(values[values.length - 1]);
        }
        break;

      default:
        break;
    }
  }

  function captureFocus() {
    inputRef.current?.focus();
  }

  function onRemove(value: string) {
    setValues(values.filter((v) => v !== value));
  }

  function isValid(value: string) {
    return EMAIL_REGEX.test(value);
  }

  function commit(newValues: Array<string>) {
    const validValues = newValues
      .map((v) => v.trim())
      .filter((v) => isValid(v));

    if (validValues.length > 0) {
      setInputValue("");
    }

    const finalValues = Array.from(new Set([...values, ...validValues]));
    setValues(finalValues);
  }

  const showPlaceholder = !inputValue && values.length === 0;

  function getInputContainerWidth() {
    if (showPlaceholder) {
      return (placeholder?.length ?? 0) * 10;
    }

    if (!showPlaceholder && isFocused) {
      return inputValue?.length * 10 || 10;
    }

    return 0;
  }

  React.useEffect(() => {
    if (hasTrailingComma(inputValue.trim())) {
      const split = inputValue.split(",");
      commit(split);
    }
  }, [inputValue]);

  return (
    <Flex
      data-color={color}
      data-radius={radius ?? theme?.radius ?? "lg"}
      data-size={size}
      className="p-input-group"
      stack
      gap={"xs"}
    >
      {label && <InputLabel>{label}</InputLabel>}
      <div
        className="p-input-control p-tag-field"
        data-focused={isFocused}
        onClick={captureFocus}
        data-appearance={appearance}
      >
        {values.map((value) => {
          return renderTag ? (
            <span key={value}>{renderTag?.(value, onRemove)}</span>
          ) : (
            <Tag
              variant={tagVariant}
              color={tagColor}
              shape={tagShape}
              size={tagSize}
              append={
                <IconButton
                  variant={"ghost"}
                  color={tagColor}
                  radius={"md"}
                  style={{ marginLeft: 8 }}
                  onPress={() => {
                    onRemove(value);
                  }}
                  size={"xs"}
                >
                  <HiX size={16} />
                </IconButton>
              }
              key={value}
            >
              {value}
            </Tag>
          );
        })}

        <Box
          style={{
            width: getInputContainerWidth(),
          }}
          className="input-container"
        >
          <input
            ref={inputRef}
            placeholder={showPlaceholder ? placeholder : ""}
            onKeyDown={onKeyDown}
            value={inputValue}
            onChange={onInputChange}
            onFocus={onInputFocus}
            onBlur={onInputBlur}
            className="p-tag-field-input"
          />
        </Box>
      </div>
      {error && <InputError>{error}</InputError>}
    </Flex>
  );
}
